import React, { useState, useEffect } from 'react';
import './App.css';
import { calculateSalary } from './utils/calculateSalary';
import Footer from './components/Footer';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';

/** Tab title / SEO (synced when language changes on calculator page) */
const DOCUMENT_TITLE_BY_LANG = {
  he: 'מחשבון שכר — NetoBro · ברו שעוזר לחשב נטו (2026)',
  en: 'Salary calculator — NetoBro · your bro for net pay (Israel, 2026)',
  ru: 'Калькулятор зарплаты — NetoBro · бро для расчёта нетто (Израиль, 2026)'
};

function App() {
  const [page, setPage] = useState('calculator');
  const [formData, setFormData] = useState({
    grossSalary: 12000,
    taxCreditPoints: 2.25,
    pensionPercentage: 6,
    taxableAdditions: 0,
    hasTrainingFund: true,
    trainingFundUseFullSalary: false,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('language');
    return savedLanguage === 'en' || savedLanguage === 'ru' || savedLanguage === 'he'
      ? savedLanguage
      : 'he';
  });

  // Handle hash-based routing
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (hash === '/privacy') {
        setPage('privacy');
      } else if (hash === '/terms') {
        setPage('terms');
      } else {
        setPage('calculator');
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    handleHashChange(); // Set initial page
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    if (page !== 'calculator') return;
    document.title = DOCUMENT_TITLE_BY_LANG[language];
  }, [language, page]);

  // Render legal pages
  if (page === 'privacy') {
    return <PrivacyPolicy />;
  }
  if (page === 'terms') {
    return <TermsOfUse />;
  }

  // Render calculator page
  const translations = {
    he: {
      title: 'מחשבון שכר — NetoBro · ברו שעוזר לחשב נטו',
      grossSalary: 'שכר גולמי חודשי (₪)',
      taxCreditPoints: 'נקודות זיכוי',
      pensionPercentage: 'פנסיה (%)',
      taxableAdditions: 'תוספות חייבות במס (לדוגמה זקיפות שכר, רכב צמוד)',
      hasTrainingFund: 'קרן השתלמות',
      trainingFundCapped: 'קרן השתלמות: ברירת מחדל (עד ₪15,712/חודש - פטור מס)',
      trainingFundFull: 'קרן השתלמות: שכר מלא (חייב מס מעל ₪15,712/חודש)',
      trainingFundInfo: 'בחר אפשרות:',
      calculate: 'חשב',
      reset: 'איפוס',
      results: 'תוצאות',
      tax: 'מס הכנסה',
      nationalInsurance: 'ביטוח לאומי',
      healthInsurance: 'ביטוח בריאות',
      pension: 'פנסיה',
      trainingFund: 'קרן השתלמות',
      netSalary: 'שכר נטו',
      effectiveTaxRate: 'שיעור מס יעיל',
      calculationDate: 'תאריך חישוב',
      breakdown: 'פירוט',
      error: 'שגיאה',
      invalidInput: 'אנא הזן ערכים תקפים'
    },
    en: {
      title: 'Salary calculator — NetoBro · your bro for net pay',
      grossSalary: 'Gross Monthly Salary (₪)',
      taxCreditPoints: 'Tax Credit Points',
      pensionPercentage: 'Pension Contribution (%)',
      taxableAdditions: 'Taxable additions (benefits in kind, company car, etc.)',
      hasTrainingFund: 'Study Fund (קרן השתלמות)',
      trainingFundCapped: 'Study Fund: Default (Capped at ₪15,712/mo - tax exempt)',
      trainingFundFull: 'Study Fund: Full Salary (taxable above ₪15,712/mo)',
      trainingFundInfo: 'Choose one:',
      calculate: 'Calculate',
      reset: 'Reset',
      results: 'Results',
      tax: 'Income Tax',
      nationalInsurance: 'National Insurance (ביטוח לאומי)',
      healthInsurance: 'Health Insurance',
      pension: 'Pension Contribution',
      trainingFund: 'Study Fund',
      netSalary: 'Net Salary (Take Home)',
      effectiveTaxRate: 'Effective Tax Rate',
      calculationDate: 'Calculation Date',
      breakdown: 'Breakdown',
      error: 'Error',
      invalidInput: 'Please enter valid values'
    },
    ru: {
      title: 'Калькулятор зарплаты — NetoBro · бро для расчёта нетто',
      grossSalary: 'Валовая ежемесячная зарплата (₪)',
      taxCreditPoints: 'Налоговые кредитные баллы',
      pensionPercentage: 'Взнос в пенсию (%)',
      taxableAdditions: 'Налогооблагаемые доплаты (доходы в натуральной форме, служебный автомобиль и т.п.)',
      hasTrainingFund: 'Фонд обучения (קרן השתלמות)',
      trainingFundCapped: 'Фонд обучения: По умолчанию (ограничено ₪15,712/мес - освобождено от налога)',
      trainingFundFull: 'Фонд обучения: Полная зарплата (налогообложение выше ₪15,712/мес)',
      trainingFundInfo: 'Выберите один:',
      calculate: 'Рассчитать',
      reset: 'Сброс',
      results: 'Результаты',
      tax: 'Подоходный налог',
      nationalInsurance: 'Национальное страхование (ביטוח לאומי)',
      healthInsurance: 'Медицинская страховка',
      pension: 'Взнос в пенсию',
      trainingFund: 'Фонд обучения',
      netSalary: 'Чистая зарплата (к получению)',
      effectiveTaxRate: 'Эффективная налоговая ставка',
      calculationDate: 'Дата расчета',
      breakdown: 'Расшифровка',
      error: 'Ошибка',
      invalidInput: 'Пожалуйста, введите правильные значения'
    }
  };

  const t = translations[language];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
      return;
    }

    // Keep empty string while editing so the input does not force "0"
    if (value === '') {
      setFormData(prev => ({
        ...prev,
        [name]: ''
      }));
      return;
    }

    const numericValue = parseFloat(value);
    setFormData(prev => ({
      ...prev,
      [name]: Number.isNaN(numericValue) ? '' : numericValue
    }));
  };

  const handleCalculate = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      if (!formData.grossSalary || formData.grossSalary <= 0) {
        setError(t.invalidInput);
        setLoading(false);
        return;
      }

      // Normalize values to numbers only at calculation time
      const normalizedFormData = {
        ...formData,
        grossSalary: Number(formData.grossSalary) || 0,
        taxCreditPoints: Number(formData.taxCreditPoints) || 0,
        pensionPercentage: Number(formData.pensionPercentage) || 0,
        taxableAdditions: Number(formData.taxableAdditions) || 0
      };

      // Calculate locally (no API call needed)
      const result = calculateSalary(normalizedFormData);
      setResult(result.data);
    } catch (err) {
      setError(err.message || 'Calculation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      grossSalary: 12000,
      pensionPercentage: 6,
      hasTrainingFund: true,
      trainingFundUseFullSalary: false,
      taxableAdditions: 0,
      taxCreditPoints: 2.25
    });
    setResult(null);
    setError(null);
  };

  const handleLanguageChange = (nextLanguage) => {
    setLanguage(nextLanguage);
    localStorage.setItem('language', nextLanguage);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(value);
  };

  return (
    <div className={`app ${language === 'he' ? 'rtl' : 'ltr'}`}>
      <header className="app-header">
        <h1>{t.title}</h1>
        <div className="language-toggle">
          <button
            className={`lang-btn ${language === 'he' ? 'active' : ''}`}
            onClick={() => {
              handleLanguageChange('he');
            }}
          >
            עברית
          </button>
          <button
            className={`lang-btn ${language === 'en' ? 'active' : ''}`}
            onClick={() => {
              handleLanguageChange('en');
            }}
          >
            English
          </button>
          <button
            className={`lang-btn ${language === 'ru' ? 'active' : ''}`}
            onClick={() => {
              handleLanguageChange('ru');
            }}
          >
            Русский
          </button>
        </div>
      </header>

      <main className="app-main">
        <div className="calculator-container">
          <div className="input-section">
            <div className="form-group">
              <label>{t.grossSalary}</label>
              <input
                type="number"
                name="grossSalary"
                value={formData.grossSalary}
                onChange={handleChange}
                placeholder="12000"
                min="0"
              />
            </div>

            <div className="form-group">
              <label>{t.taxCreditPoints}</label>
              <input
                type="number"
                name="taxCreditPoints"
                value={formData.taxCreditPoints}
                onChange={handleChange}
                placeholder="2.25"
                step="0.25"
                min="0"
              />
            </div>

            <div className="form-group">
              <label>{t.pensionPercentage}</label>
              <input
                type="number"
                name="pensionPercentage"
                value={formData.pensionPercentage}
                onChange={handleChange}
                min="0"
                max="100"
                step="0.1"
                placeholder="6"
              />
            </div>

            <div className="form-group">
              <label>{t.taxableAdditions}</label>
              <input
                type="number"
                name="taxableAdditions"
                value={formData.taxableAdditions}
                onChange={handleChange}
                min="0"
                step="1"
                placeholder="0"
              />
            </div>

            <div className="form-group training-fund-options">
              <label>
                <input
                  type="checkbox"
                  name="hasTrainingFund"
                  checked={formData.hasTrainingFund}
                  onChange={handleChange}
                />
                {t.hasTrainingFund}
              </label>
              {formData.hasTrainingFund && (
                <div className="radio-group" style={{ marginTop: '0.75rem' }}>
                  <label>
                    <input
                      type="radio"
                      name="trainingFundUseFullSalary"
                      checked={formData.trainingFundUseFullSalary === false}
                      onChange={() => setFormData(prev => ({
                        ...prev,
                        trainingFundUseFullSalary: false
                      }))}
                    />
                    {t.trainingFundCapped}
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="trainingFundUseFullSalary"
                      checked={formData.trainingFundUseFullSalary === true}
                      onChange={() => setFormData(prev => ({
                        ...prev,
                        trainingFundUseFullSalary: true
                      }))}
                    />
                    {t.trainingFundFull}
                  </label>
                </div>
              )}
            </div>

            <div className="button-group">
              <button className="btn btn-primary" onClick={handleCalculate} disabled={loading}>
                {loading ? 'Calculating...' : t.calculate}
              </button>
              <button className="btn btn-secondary" onClick={handleReset}>
                {t.reset}
              </button>
            </div>
          </div>

          {error && (
            <div className="error-message">
              <strong>{t.error}:</strong> {error}
            </div>
          )}

          {result && (
            <div className="results-section">
              <h2>{t.results}</h2>

              <div className="results-summary">
                <div className="result-item main-result">
                  <span className="label">{t.netSalary}</span>
                  <span className="value">{formatCurrency(result.netSalary)}</span>
                </div>
              </div>

              <div className="results-breakdown">
                <h3>{t.breakdown}</h3>
                <div className="breakdown-grid">
                  <div className="breakdown-item">
                    <span className="breakdown-label">{t.grossSalary}</span>
                    <span className="breakdown-value">{formatCurrency(result.grossSalary)}</span>
                  </div>

                  <div className="breakdown-item deduction">
                    <span className="breakdown-label">{t.tax}</span>
                    <span className="breakdown-value">-{formatCurrency(result.tax)}</span>
                  </div>

                  <div className="breakdown-item deduction">
                    <span className="breakdown-label">{t.nationalInsurance}</span>
                    <span className="breakdown-value">-{formatCurrency(result.nationalInsurance)}</span>
                  </div>

                  <div className="breakdown-item deduction">
                    <span className="breakdown-label">{t.healthInsurance}</span>
                    <span className="breakdown-value">-{formatCurrency(result.healthInsurance)}</span>
                  </div>

                  <div className="breakdown-item deduction">
                    <span className="breakdown-label">{t.pension}</span>
                    <span className="breakdown-value">-{formatCurrency(result.pension)}</span>
                  </div>

                  <div className="breakdown-item deduction">
                    <span className="breakdown-label">{t.trainingFund}</span>
                    <span className="breakdown-value">-{formatCurrency(result.trainingFund)}</span>
                  </div>
                </div>
              </div>

              <div className="results-info">
                <div className="info-item">
                  <span>{t.effectiveTaxRate}:</span>
                  <strong>{result.effectiveTaxRate}%</strong>
                </div>
                <div className="info-item">
                  <span>{t.calculationDate}:</span>
                  <strong>{new Date(result.calculationDate).toLocaleDateString()}</strong>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default App;
